import { useState, useRef, useEffect } from "react";
import PrizePicker from "../PrizePicker.jsx";
import PlushImg from "../PlushImg.jsx";
import { LOUD_COLORS, getType, getColor, SIZES, plushFilter } from "../prizes.js";
import { t } from "../i18n.js";
import { ensureCtx, out } from "../audio.js";

/* ==================== LYKKEHJULET – the lucky wheel ==========================
   Pick 3 of 20 numbers, spin the big colourful wheel. The number chips share
   their colour with their segment on the wheel. Pointer lands on one of your
   numbers = a medium plush. No cooldown — spin away!
================================================================================ */

const W = 360, WHEEL_H = 400;
const CX = W / 2, CY = 208, R = 158;
const SEGS = 20;
const SEG_A = (Math.PI * 2) / SEGS;
const segColor = (i) => LOUD_COLORS[i % LOUD_COLORS.length].css;

/* ---- tiny synth sounds ---- */
function makeAudio() {
  let ctx = null;
  const ensure = () => (ctx = ensureCtx());
  const tone = (f, t0, dur, type = "sine", vol = 0.12) => {
    const c = ensure(); if (!c) return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f, t0);
    g.gain.setValueAtTime(vol, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g); g.connect(out()); o.start(t0); o.stop(t0 + dur);
  };
  return {
    unlock: ensure,
    click() { const c = ensure(); if (c) tone(900, c.currentTime, 0.03, "square", 0.05); },
    buzzer() { const c = ensure(); if (c) tone(110, c.currentTime, 0.7, "sawtooth", 0.16); },
    win() {
      const c = ensure(); if (!c) return;
      [523, 659, 784, 1046].forEach((f, i) => tone(f, c.currentTime + i * 0.14, 0.3, "triangle", 0.14));
    },
  };
}

export default function Wheel({ onExit }) {
  const [picks, setPicks] = useState([]);        // up to 3 numbers (1-20)
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);    // {n, hit}
  const [claimed, setClaimed] = useState(null);

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const stateRef = useRef({ rot: 0, lastSeg: -1 });
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = makeAudio();

  const togglePick = (n) => {
    if (spinning) return;
    setPicks((p) => p.includes(n) ? p.filter((x) => x !== n) : (p.length < 3 ? [...p, n] : p));
  };

  const spin = () => {
    if (spinning || picks.length !== 3) return;
    audioRef.current.unlock();
    setResult(null);
    setClaimed(null);
    setSpinning(true);

    const target = Math.floor(Math.random() * SEGS);       // 0-indexed segment
    const s = stateRef.current;
    // land the target segment's centre under the pointer (top, -90°)
    const targetRot = -Math.PI / 2 - (target + 0.5) * SEG_A;
    const current = s.rot % (Math.PI * 2);
    const delta = ((targetRot - current) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
    const total = delta + Math.PI * 2 * 6;                  // 6 ekstra omgange
    const startRot = s.rot;
    const DUR = 4600;
    const t0 = performance.now();

    const animate = (now) => {
      const k = Math.min(1, (now - t0) / DUR);
      const ease = 1 - Math.pow(1 - k, 3);                  // cubic ease-out
      s.rot = startRot + total * ease;
      // tick-lyd når et nyt segment passerer pilen
      const seg = Math.floor(((-Math.PI / 2 - s.rot) / SEG_A % SEGS + SEGS) % SEGS);
      if (seg !== s.lastSeg) { s.lastSeg = seg; audioRef.current.click(); }
      if (k < 1) { requestAnimationFrame(animate); return; }
      const n = target + 1;
      const hit = picks.includes(n);
      setSpinning(false);
      setResult({ n, hit });
      if (hit) audioRef.current.win(); else audioRef.current.buzzer();
    };
    requestAnimationFrame(animate);
  };

  /* ================= canvas: draw loop ================= */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const maxW = Math.min((wrap.parentElement ? wrap.parentElement.clientWidth : W) * 0.96, 440);
      const scale = maxW / W;
      canvas.style.width = W * scale + "px";
      canvas.style.height = WHEEL_H * scale + "px";
      canvas.width = W * dpr * scale;
      canvas.height = WHEEL_H * dpr * scale;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);

    /* ---- static night-carnival backdrop, baked once (2x offscreen):
       sky, ferris wheel + tent silhouettes, bokeh, string lights, ground ---- */
    const rnd = (i) => { const s = Math.sin(i * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s); };
    const bg = document.createElement("canvas");
    bg.width = W * 2; bg.height = WHEEL_H * 2;
    {
      const b = bg.getContext("2d");
      b.scale(2, 2);
      // NB: baked on TRANSPARENT canvas — the page CSS gradient is the night sky,
      // so the canvas edges never show as a seam.

      // distant ferris wheel, clipped by the right edge (reads as "continues outside")
      b.strokeStyle = "rgba(255,190,120,0.12)";
      b.lineWidth = 2;
      b.beginPath(); b.arc(330, 140, 64, 0, Math.PI * 2); b.stroke();
      b.lineWidth = 1.2;
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        b.beginPath(); b.moveTo(330, 140);
        b.lineTo(330 + Math.cos(a) * 64, 140 + Math.sin(a) * 64); b.stroke();
        b.fillStyle = "rgba(255,205,130,0.30)";
        b.beginPath(); b.arc(330 + Math.cos(a) * 64, 140 + Math.sin(a) * 64, 2.2, 0, Math.PI * 2); b.fill();
      }

      // tent silhouette, left edge
      b.fillStyle = "rgba(190,80,70,0.16)";
      b.beginPath();
      b.moveTo(-30, 250); b.lineTo(28, 168); b.lineTo(86, 250);
      b.closePath(); b.fill();
      b.strokeStyle = "rgba(255,190,120,0.14)";
      b.beginPath(); b.moveTo(28, 168); b.lineTo(28, 156); b.stroke();

      // bokeh glow from the midway
      for (let i = 0; i < 16; i++) {
        const x = rnd(i * 3) * W;
        const y = 60 + rnd(i * 7 + 1) * 260;
        const r = 5 + rnd(i * 11 + 2) * 16;
        const warm = rnd(i * 5 + 3);
        const col = warm < 0.6 ? "255,200,110" : warm < 0.8 ? "255,140,90" : "150,200,255";
        const gl = b.createRadialGradient(x, y, 0, x, y, r);
        gl.addColorStop(0, `rgba(${col},${0.10 + rnd(i * 13) * 0.08})`);
        gl.addColorStop(1, `rgba(${col},0)`);
        b.fillStyle = gl;
        b.beginPath(); b.arc(x, y, r, 0, Math.PI * 2); b.fill();
      }

      // string lights across the top
      for (let w = 0; w < 2; w++) {
        const y0 = 6 + w * 15, cy2 = 26 + w * 17;
        b.strokeStyle = "rgba(0,0,0,0.5)";
        b.lineWidth = 1;
        b.beginPath(); b.moveTo(0, y0);
        b.quadraticCurveTo(W / 2, cy2, W, y0 + 2); b.stroke();
        for (let k = 1; k < 12; k++) {
          const u = k / 12, x = u * W;
          const y = (1 - u) * (1 - u) * y0 + 2 * (1 - u) * u * cy2 + u * u * (y0 + 2) + 3;
          b.fillStyle = "rgba(255,225,150,0.9)";
          b.shadowColor = "#ffd75e"; b.shadowBlur = 7;
          b.beginPath(); b.arc(x, y, 2.4, 0, Math.PI * 2); b.fill();
          b.shadowBlur = 0;
        }
      }

      // soft ground shadow under the wheel
      b.save(); b.translate(CX, 383); b.scale(1, 0.12);
      const gs = b.createRadialGradient(0, 0, 10, 0, 0, 150);
      gs.addColorStop(0, "rgba(0,0,0,0.5)");
      gs.addColorStop(1, "rgba(0,0,0,0)");
      b.fillStyle = gs;
      b.beginPath(); b.arc(0, 0, 150, 0, Math.PI * 2); b.fill();
      b.restore();
    }

    /* gradients reused every frame (logical coords, unaffected by fit) */
    const rimGrad = ctx.createLinearGradient(0, CY - R - 20, 0, CY + R + 20);
    rimGrad.addColorStop(0, "#8a5c33"); rimGrad.addColorStop(0.5, "#5f3d1f"); rimGrad.addColorStop(1, "#3f2412");
    const shadeGrad = ctx.createRadialGradient(CX, CY, 24, CX, CY, R);
    shadeGrad.addColorStop(0, "rgba(0,0,0,0.35)");
    shadeGrad.addColorStop(0.4, "rgba(0,0,0,0.10)");
    shadeGrad.addColorStop(0.8, "rgba(0,0,0,0)");
    shadeGrad.addColorStop(1, "rgba(0,0,0,0.12)");
    const sheenGrad = ctx.createRadialGradient(CX, CY - R * 0.75, 10, CX, CY - R * 0.75, R * 1.5);
    sheenGrad.addColorStop(0, "rgba(255,255,255,0.16)");
    sheenGrad.addColorStop(0.5, "rgba(255,255,255,0.03)");
    sheenGrad.addColorStop(1, "rgba(255,255,255,0)");
    const hubGrad = ctx.createRadialGradient(CX - 7, CY - 9, 3, CX, CY, 30);
    hubGrad.addColorStop(0, "#f7dd96"); hubGrad.addColorStop(0.55, "#d9ab4e"); hubGrad.addColorStop(1, "#9a6a1e");
    const hubInner = ctx.createRadialGradient(CX - 5, CY - 6, 2, CX, CY, 22);
    hubInner.addColorStop(0, "#e3bc62"); hubInner.addColorStop(1, "#8a5c1e");
    const ptrGrad = ctx.createLinearGradient(CX - 15, 0, CX + 15, 0);
    ptrGrad.addColorStop(0, "#f4f6f8"); ptrGrad.addColorStop(0.5, "#c3c9cf"); ptrGrad.addColorStop(1, "#7e868e");

    const draw = () => {
      const s = stateRef.current;
      const now = performance.now();
      ctx.clearRect(0, 0, W, WHEEL_H);
      ctx.drawImage(bg, 0, 0, W, WHEEL_H);

      // wood backing behind the whole wheel
      ctx.beginPath(); ctx.arc(CX, CY, R + 20, 0, Math.PI * 2);
      ctx.fillStyle = rimGrad; ctx.fill();

      // segments with gold separators
      for (let i = 0; i < SEGS; i++) {
        const a0 = s.rot + i * SEG_A, a1 = a0 + SEG_A;
        ctx.beginPath();
        ctx.moveTo(CX, CY);
        ctx.arc(CX, CY, R, a0, a1);
        ctx.closePath();
        ctx.fillStyle = segColor(i);
        ctx.fill();
        ctx.strokeStyle = "rgba(201,162,75,0.9)";
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // leather shading + glossy sheen over the wheel face
      ctx.beginPath(); ctx.arc(CX, CY, R, 0, Math.PI * 2);
      ctx.fillStyle = shadeGrad; ctx.fill();
      ctx.beginPath(); ctx.arc(CX, CY, R, 0, Math.PI * 2);
      ctx.fillStyle = sheenGrad; ctx.fill();

      // numbers, drawn over the shading so they stay crisp
      for (let i = 0; i < SEGS; i++) {
        const am = s.rot + (i + 0.5) * SEG_A;
        ctx.save();
        ctx.translate(CX + Math.cos(am) * R * 0.8, CY + Math.sin(am) * R * 0.8);
        ctx.rotate(am + Math.PI / 2);
        ctx.font = "bold 18px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.lineWidth = 3; ctx.strokeStyle = "rgba(70,30,5,0.65)";
        ctx.strokeText(String(i + 1), 0, 0);
        ctx.fillStyle = "#F6EED9";
        ctx.fillText(String(i + 1), 0, 0);
        ctx.restore();
      }

      // mahogany rim with gold pinstripes
      ctx.beginPath(); ctx.arc(CX, CY, R + 13, 0, Math.PI * 2);
      ctx.lineWidth = 14; ctx.strokeStyle = rimGrad; ctx.stroke();
      ctx.beginPath(); ctx.arc(CX, CY, R + 20, 0, Math.PI * 2);
      ctx.lineWidth = 2.5; ctx.strokeStyle = "#e9c46a"; ctx.stroke();
      ctx.beginPath(); ctx.arc(CX, CY, R + 6, 0, Math.PI * 2);
      ctx.lineWidth = 2; ctx.strokeStyle = "#c9a24b"; ctx.stroke();
      ctx.lineWidth = 1;

      // marquee bulbs on the rim housing: static positions, gentle twinkle
      for (let i = 0; i < 24; i++) {
        const a = (i / 24) * Math.PI * 2;
        const tw = 0.72 + 0.28 * Math.sin(now / 320 + i * 2.4);
        ctx.beginPath();
        ctx.arc(CX + Math.cos(a) * (R + 13), CY + Math.sin(a) * (R + 13), 3.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,233,168,${tw})`;
        ctx.shadowColor = "#ffd75e"; ctx.shadowBlur = 8 * tw;
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      // brass hub with an engraved circus tent
      ctx.beginPath(); ctx.arc(CX, CY + 2, 29, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(0,0,0,0.30)"; ctx.fill();
      ctx.beginPath(); ctx.arc(CX, CY, 28, 0, Math.PI * 2);
      ctx.fillStyle = hubGrad; ctx.fill();
      ctx.strokeStyle = "#6b4a12"; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.beginPath(); ctx.arc(CX, CY, 21, 0, Math.PI * 2);
      ctx.fillStyle = hubInner; ctx.fill();
      ctx.strokeStyle = "rgba(90,60,10,0.8)"; ctx.lineWidth = 1; ctx.stroke();
      ctx.strokeStyle = "#5f3a10"; ctx.fillStyle = "rgba(95,58,16,0.9)";
      ctx.lineWidth = 1.6; ctx.lineJoin = "round";
      ctx.beginPath();                                     // scalloped roof
      ctx.moveTo(CX - 11, CY + 2);
      ctx.lineTo(CX, CY - 10);
      ctx.lineTo(CX + 11, CY + 2);
      ctx.arc(CX + 7.3, CY + 2, 3.7, 0, Math.PI);
      ctx.arc(CX, CY + 2, 3.7, 0, Math.PI);
      ctx.arc(CX - 7.3, CY + 2, 3.7, 0, Math.PI);
      ctx.closePath(); ctx.fill();
      ctx.beginPath();                                     // body + entrance
      ctx.moveTo(CX - 7, CY + 4); ctx.lineTo(CX - 5.5, CY + 11);
      ctx.lineTo(CX + 5.5, CY + 11); ctx.lineTo(CX + 7, CY + 4);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(CX - 2.5, CY + 11); ctx.lineTo(CX, CY + 6.5); ctx.lineTo(CX + 2.5, CY + 11);
      ctx.stroke();
      ctx.beginPath(); ctx.moveTo(CX, CY - 10); ctx.lineTo(CX, CY - 14); ctx.stroke();  // flag
      ctx.beginPath();
      ctx.moveTo(CX, CY - 14); ctx.lineTo(CX + 5, CY - 12.2); ctx.lineTo(CX, CY - 10.6);
      ctx.closePath(); ctx.fill();

      // chrome pointer (top)
      const py0 = CY - R - 22, py1 = CY - R + 12;
      ctx.beginPath();
      ctx.moveTo(CX - 13, py0 + 3); ctx.lineTo(CX + 17, py0 + 3); ctx.lineTo(CX + 2, py1 + 3);
      ctx.closePath();
      ctx.fillStyle = "rgba(0,0,0,0.30)"; ctx.fill();
      ctx.beginPath();
      ctx.moveTo(CX - 15, py0); ctx.lineTo(CX + 15, py0); ctx.lineTo(CX, py1);
      ctx.closePath();
      ctx.fillStyle = ptrGrad; ctx.fill();
      ctx.strokeStyle = "#3a3f44"; ctx.lineWidth = 2; ctx.stroke();
      ctx.beginPath(); ctx.moveTo(CX - 9, py0 + 4); ctx.lineTo(CX - 2, py1 - 8);
      ctx.strokeStyle = "rgba(255,255,255,0.7)"; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.lineWidth = 1;

      raf = requestAnimationFrame(draw);
    };
    let raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      ro.disconnect();
    };
  }, []);

  /* ================= UI ================= */
  const missing = 3 - picks.length;

  return (
    <div className="wh-root ingame">
      <style>{css}</style>

      <header className="booth">
        <button className="back" onClick={onExit}>{t("back")}</button>
        <h1>{t("tile_wheel").toUpperCase()}</h1>
        <div className="signs">
          <span className="sign cream">{t("sign_free")}</span>
          <span className="sign red">{t("w_sign2")}</span>
        </div>
      </header>

      <div ref={wrapRef} className="boardwrap">
        <canvas ref={canvasRef} />
      </div>

      <div className="wh-status">
        {spinning ? t("w_spinning") : missing > 0 ? t("w_pick", { n: missing }) : t("w_ready")}
      </div>

      <div className="wh-chips">
        {Array.from({ length: SEGS }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            className={"wh-chip" + (picks.includes(n) ? " sel" : "")}
            style={{ background: segColor(n - 1) }}
            onClick={() => togglePick(n)}
            disabled={spinning}
          >
            {n}
          </button>
        ))}
      </div>

      <button className="cta wh-spin" disabled={picks.length !== 3 || spinning} onClick={spin}>
        {t("w_spin")}
      </button>
      <p className="wh-note">
        <img className="wh-teddy" src="/images/thumbs/teddy_wave.png" alt="" />
        <span>{t("w_prizenote1")}<br />{t("w_prizenote2")}</span>
      </p>

      {result && !result.hit && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon dark">{t("w_lose")}</div>
            <p className="winline">{t("w_loseline", { n: result.n })}</p>
            <button className="cta" onClick={() => { setResult(null); setPicks([]); }}>{t("try_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {result && result.hit && !claimed && (
        <PrizePicker size="mellem" game="Lykkehjulet" onDone={(p) => setClaimed(p)} />
      )}

      {result && result.hit && claimed && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon">{t("w_win")}</div>
            <p className="winline">{t("w_winline", { n: result.n })}</p>
            <p className="prizeline">
              <PlushImg className="prizeemoji" type={claimed.type} color={claimed.color} px={SIZES[claimed.size].pick} />
              <br />
              {t("your_prize")} <b>{t("color_" + claimed.color).toLowerCase()} {t("type_" + claimed.type).toLowerCase()}</b> ({t("size_" + claimed.size).toLowerCase()}) {t("in_room")}
            </p>
            <button className="cta" onClick={() => { setResult(null); setClaimed(null); setPicks([]); }}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const css = `
.wh-root{
  --red:#C8352E; --cream:#F6EED9; --ink:#22301c;
  min-height:100vh; background:#241408;
  background-image:linear-gradient(180deg,#1c2a4e 0%,#3a2033 46%,#241408 82%);
  font-family:'Nunito',system-ui,sans-serif; color:var(--cream);
  display:flex; flex-direction:column; align-items:center; padding-bottom:24px;
  -webkit-user-select:none; user-select:none; touch-action:manipulation;
}
.booth{width:100%; max-width:520px; text-align:center; padding:10px 12px 2px; position:relative;}
.back{
  position:absolute; left:10px; top:10px; z-index:2;
  background:var(--cream); color:var(--ink); border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #b3a880;
}
.booth h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(22px,6vw,34px);
  margin:0; letter-spacing:2px; color:var(--cream);
  text-shadow:0 3px 0 #7e1f1a, 0 6px 12px rgba(0,0,0,.45);
}
.signs{display:flex; gap:8px; justify-content:center; margin-top:4px; flex-wrap:wrap;}
.sign{font-weight:800; font-size:12px; padding:4px 10px; border-radius:3px; letter-spacing:.5px;}
.sign.cream{background:var(--cream); color:var(--ink); border:1px solid #c9bf9f;}
.sign.red{background:var(--red); color:#fff;}
.boardwrap{width:auto; max-width:96%;}
.boardwrap canvas{display:block;}
.wh-status{font-weight:800; font-size:16px; margin:0 0 8px; text-shadow:0 2px 4px rgba(0,0,0,.5);}
.wh-chips{
  width:min(94%,440px); display:grid; grid-template-columns:repeat(10, 1fr); gap:6px;
}
.wh-chip{
  aspect-ratio:1; border:2px solid rgba(240,205,120,.85); border-radius:50%; cursor:pointer;
  font-family:inherit; font-weight:800; font-size:14px; color:#fff;
  text-shadow:0 1px 2px rgba(0,0,0,.55);
  box-shadow:inset 0 3px 4px rgba(255,255,255,.45), inset 0 -4px 6px rgba(0,0,0,.4), 0 2px 5px rgba(0,0,0,.5);
  transition:transform .1s;
}
.wh-chip.sel{
  transform:scale(1.12);
  outline:3px solid #ffd75e; outline-offset:2px;
  box-shadow:0 0 16px rgba(255,215,94,.85), inset 0 3px 4px rgba(255,255,255,.45), inset 0 -4px 6px rgba(0,0,0,.4);
}
.wh-chip:disabled{opacity:.6; cursor:default;}
.cta{
  width:min(94%,440px); margin-top:14px; padding:14px; font-size:19px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:var(--red); color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cta:disabled{filter:saturate(.4) brightness(.8); cursor:default; transform:none;}
.cta.alt{background:#6b4a2e; box-shadow:0 4px 0 #3f2a16; font-size:15px; padding:11px; margin-top:10px;}
.cta.alt:active{box-shadow:0 1px 0 #3f2a16;}
.cta.wh-spin{
  position:relative; overflow:hidden;
  background:linear-gradient(180deg,#f7df9a 0%,#e9b94e 48%,#c98f2a 100%);
  color:#4a2c00; text-shadow:0 1px 0 rgba(255,255,255,.45);
  border:4px solid #4a2c16; border-radius:12px;
  box-shadow:0 4px 0 #2e1a0c, inset 0 2px 3px rgba(255,255,255,.5), inset 0 -3px 5px rgba(0,0,0,.25), 0 0 14px rgba(255,196,60,.35);
  animation:wh-pulse 1.6s ease-in-out infinite;
}
.cta.wh-spin::after{
  content:""; position:absolute; top:0; bottom:0; left:-70%; width:45%;
  background:linear-gradient(105deg, transparent 0%, rgba(255,255,255,.55) 50%, transparent 100%);
  transform:skewX(-20deg);
  animation:wh-sheen 2.4s ease-in-out infinite;
}
.cta.wh-spin:active{transform:translateY(3px); box-shadow:0 1px 0 #2e1a0c, inset 0 2px 3px rgba(255,255,255,.5), inset 0 -3px 5px rgba(0,0,0,.25), 0 0 14px rgba(255,196,60,.35);}
.cta.wh-spin:disabled{animation:none; box-shadow:0 4px 0 #2e1a0c, inset 0 2px 3px rgba(255,255,255,.5), inset 0 -3px 5px rgba(0,0,0,.25);}
.cta.wh-spin:disabled::after{display:none;}
@keyframes wh-sheen{ 0%{left:-70%;} 55%{left:120%;} 100%{left:120%;} }
@keyframes wh-pulse{
  0%,100%{box-shadow:0 4px 0 #2e1a0c, inset 0 2px 3px rgba(255,255,255,.5), inset 0 -3px 5px rgba(0,0,0,.25), 0 0 10px rgba(255,196,60,.3);}
  50%{box-shadow:0 4px 0 #2e1a0c, inset 0 2px 3px rgba(255,255,255,.5), inset 0 -3px 5px rgba(0,0,0,.25), 0 0 26px rgba(255,214,90,.75);}
}
.wh-note{
  display:flex; align-items:center; justify-content:center; gap:10px; text-align:left;
  font-size:12px; font-weight:800; opacity:.85; margin:12px 0 0;
}
.wh-teddy{width:42px; height:42px; object-fit:contain; filter:drop-shadow(0 2px 3px rgba(0,0,0,.5));}
.overlay{
  position:fixed; inset:0; background:rgba(14,8,4,.78); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.card{
  width:min(88%,380px); background:var(--cream); color:var(--ink);
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:22px; letter-spacing:1px;
  background:var(--red); color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.ribbon.dark{background:#4a3520;}
.winline{font-size:16px; font-weight:800; margin:4px 0 12px;}
.prizeline{font-size:14px; font-weight:800; margin:0 0 12px; line-height:1.5;}
.prizeemoji{line-height:1.2; display:inline-block;}
`;
